import React from 'react'
import { Information, CheckCircle, CloseCircle } from '@indeed/ifl-icons'
import { Tooltip } from '@material-ui/core'
import moment from 'moment'

import { FILTERS_SCHEMA } from './schema'

import { SchemaProps, WorkerFilterResultsData } from './types'
import S from './MatchingExplainability.module.scss'

const formatFilterName = (name?: string) => name?.replace(/_/g, ' ') || 'Unknown filter'

const getFallbackSchema = (filter: any) => ({
  title: formatFilterName(filter?.name),
  tooltip: 'This filter was returned by flex-core but ACP does not have custom display copy for it yet.',
  code: filter?.name || 'unknown_filter',
  isUS: false,
  description: () => filter?.description || 'No additional explanation is available.',
})

// True only when the SINGLE reason the worker was filtered out is that they have
// already been offered the selected shift(s). In that case the worker legitimately
// won't appear in the browse list (they are already in the "Offered candidates" list —
// changing that is a MARS change), so we surface it as info rather than a red failure.
const isAlreadyOfferedOnly = (data: WorkerFilterResultsData): boolean => {
  if (data?.metadata?.number_of_failed_filters !== 1) return false
  const availability: any = data?.filters?.find(
    (f: any) => (f?.filterData?.name ?? f?.name) === 'availability_filter',
  )
  const extraInfo = (availability?.filterData ?? availability)?.extra_info
  const status = extraInfo?.worker_shift_status
  if (!extraInfo || !status) return false
  const len = (v?: any[]) => v?.length ?? 0
  // Non-blocking signals are deliberately excluded from the gate: `booked_time_ranges`
  // lists ALL of the worker's bookings (including unrelated ones), and cancelled shifts
  // do not block availability in flex-core — so neither should keep a purely
  // already-offered worker in the red failure state.
  return (
    len(status.offered_shift_ids) > 0 &&
    len(status.booked_shift_ids) === 0 &&
    len(status.approved_absence_shift_ids) === 0 &&
    len(extraInfo.conflicting_shift_ids) === 0 &&
    len(extraInfo.within_two_hours_shift_ids) === 0 &&
    len(extraInfo.late_cancellation_shift_ids) === 0 &&
    len(extraInfo.unavailable_timeslots) === 0
  )
}

export const getWorkersFilterResults = (data: WorkerFilterResultsData) => {
  const passed = data.metadata.number_of_passed_filters === data.metadata.number_of_filters

  // Already-offered is not a genuine failure — the worker is simply in the Offered
  // candidates list — so show an info state instead of the red failure circle.
  if (!passed && isAlreadyOfferedOnly(data)) {
    return (
      <div>
        Already offered — can&apos;t be re-offered (see Offered candidates)
        <span data-testid="already_offered_icon" className={S['worker-filter-results__container']}>
          <Information color="blue" size="sm" />
        </span>
      </div>
    )
  }

  return (
    <div>
      {passed ? (
        <>
          <b>{data.metadata.number_of_passed_filters}</b> out of {data.metadata.number_of_filters} passed
          <span data-testid="passed_icon" className={S['worker-filter-results__container']}>
            <CheckCircle color="green" size="sm" />
          </span>
        </>
      ) : (
        <>
          <b>{data.metadata.number_of_failed_filters}</b> out of {data.metadata.number_of_filters} failed
          <span data-testid="failed_icon" className={S['worker-filter-results__container']}>
            <CloseCircle color="red" size="sm" />
          </span>
        </>
      )}
    </div>
  )
}

export const getFiltersData = (matchingExplainability: { filters: { name: string }[] }) => {
  const filters = matchingExplainability?.filters
  const isBlockedByClient = filters?.find((filter: any) => filter.name === 'blocked_by_employer_filter')
  return filters
    ?.map((filter: any) => {
      const schema =
        FILTERS_SCHEMA.find((filterSchema: any) => filterSchema.code === filter.name) ??
        getFallbackSchema(filter)
      return { filterData: filter, schema }
    })
    .filter((filter: any) => {
      // We display only blocked by client in case the worker was blocked by employer & venue same time
      return !!isBlockedByClient ? filter?.schema?.code !== 'blocked_at_work_location_filter' : true
    })
}

export const getReasonValue =
  ({
    workerId,
    roles,
    skills,
    employerName,
    employerID,
    venues,
    cities,
    actions,
    userData,
    token,
    venueID,
  }: Partial<SchemaProps>) =>
  (
    _: any,
    subRowData: {
      schema: { title: string; tooltip: string; description: (data: Partial<SchemaProps>) => void }
    },
  ) => {
    return subRowData?.schema?.description({
      workerId,
      subRowData,
      roles,
      skills,
      employerName,
      employerID,
      venues,
      cities,
      actions,
      userData,
      venueID,
      token,
    })
  }

export const getTitleWithTooltip = (_: {}, subRowData: { schema: { title: string; tooltip: string } }) => {
  return (
    <div>
      <div className={S['title-with-tooltip__container']}>{subRowData?.schema?.title}</div>
      <div className={S['title-with-tooltip__tooltip']}>
        <Tooltip
          title={<div className={S['title-with-tooltip__font']}>{subRowData?.schema?.tooltip}</div>}
          placement="top"
          arrow
        >
          <Information />
        </Tooltip>
      </div>
    </div>
  )
}

export const getSkillTitles = (requiredSkillIds: number[], actualSkillIds: number[], data: SchemaProps) => {
  return requiredSkillIds
    ?.filter((requiredId: number) => !actualSkillIds?.includes(requiredId))
    .map((id: number) => data?.skills?.[id]?.title)
    .join(', ')
}

export const getConflictDates = (resultConflict: string[]) => {
  const resultConflictLengh = resultConflict?.length
  const dateFormat = 'YYYY-MM-DDTHH:mm:ssZZ'
  const startDate = moment(resultConflict?.[0]?.substring(0, 10)).format(dateFormat).replace(/\+/g, '%2B')
  const endDate = moment(resultConflict?.[resultConflictLengh - 1]?.substring(0, 10))
    .add(1, 'days')
    .format(dateFormat)
    .replace(/\+/g, '%2B')

  return { startDate, endDate }
}
